/**
 * ============================================================================
 * RoleManagementPage — 역할(Role) 마스터 관리
 * ============================================================================
 *
 * 【역할】
 *   시스템 역할(Role) 마스터 CRUD — RBAC 의 핵심 엔티티.
 *   사용자×역할(M:M), 역할×메뉴 권한, 역할×서비스 권한의 기준 키가 된다.
 *   목록 그리드 + Modal 편집 폼 패턴.
 *
 * 【주요 기능】
 *   - 검색: roleName 키워드 필터
 *   - CRUD: 추가(Modal) / 수정(Modal) / 삭제(soft) / 벌크 저장
 *   - 표시: 역할별 할당 사용자 수(userCount), 사용여부 Tag, _state 신규/수정 Tag
 *
 * 【Backend 호출】
 *   - POST /api/v1/transaction (service=adminRole/searchRoleList) — 역할 목록
 *   - POST /api/v1/transaction (service=adminRole/saveRole)       — 벌크 저장
 *
 * 【DataSet/Hook】
 *   - useDataSet<RoleRow>() — ds_role : 역할 목록 (PK: roleId)
 *   - useTransaction()      — RPC 통합 호출
 *   - useLabel()            — i18n 라벨(t)
 *
 * 【사용처】 router/index.tsx — `{ path: '/admin/role', element: <RoleManagementPage /> }`
 * 【포팅 원본】 vue-spring-fw `frontend/src/pages/PageRoleManagement.vue`
 * 【의존성】 antd Form/Input/Modal/Switch/Tag, framework hooks
 */
import { useEffect, useMemo, useState } from 'react'
import { Form, Input, Modal, Switch, Tag } from 'antd'
import AppPage from '@/components/common/AppPage'
import DataTable from '@/components/common/DataTable'
import FormInput from '@/components/common/FormInput'
import { useDataSet, useTransaction } from '@/framework'
import { useLabel } from '@/framework/useLabel'
import { makeColumns, type ColumnSpec } from '@/framework/grid/makeColumns'
import type { TrackedRow } from '@/framework/useDataSet'

/** 역할 1행 — RBAC 기준 엔티티. */
interface RoleRow {
  /** 역할 PK (대문자 영숫자, 예: "ADMIN", "USER", "MANAGER"). */
  roleId: string
  /** 역할 표시명 (예: "관리자", "일반 사용자"). */
  roleName: string
  /** 역할 설명 (선택, 다국어 지원 안 함). */
  description?: string
  /** 사용여부 ('Y'=활성 / 'N'=비활성). */
  useYn: 'Y' | 'N'
  /** 해당 역할에 매핑된 사용자 수 (서버 집계, 표시 전용). */
  userCount?: number
}

/**
 * RoleManagementPage — 컴포넌트/함수 (자동 생성된 JSDoc 스텁).
 * @returns 렌더 결과 또는 반환값
 */
export default function RoleManagementPage() {
  const ds = useDataSet<RoleRow>()
  const { transaction } = useTransaction()
  const [searchForm] = Form.useForm()
  const [modalOpen, setModalOpen] = useState(false)
  const [editingIdx, setEditingIdx] = useState<number>(-1)
  const [form] = Form.useForm()

  /**
   * 역할 검색 — adminRole/searchRoleList → ds.
   * - 1) 외부 values 또는 searchForm 값 수집
   * - 2) ds_search 로 inds 전송, ds_role 로 outds 수신
   * @param values 외부 검색 폼 값 (생략 시 내부 searchForm 사용)
   */
  const search = (values?: Record<string, unknown>) =>
    transaction({
      id: 'search',
      service: 'adminRole/searchRoleList',
      action: 'search',
      inds: { ds_search: values ?? searchForm.getFieldsValue() },
      outds: { ds_role: ds }
    })

  useEffect(() => {
    search()
  }, [])

  /** 추가 Modal open — editingIdx=-1 (신규 모드), 폼 초기화 + useYn 기본값 'Y'. */
  const openAdd = () => {
    setEditingIdx(-1)
    form.resetFields()
    form.setFieldsValue({ useYn: 'Y' })
    setModalOpen(true)
  }

  /** 수정 Modal open — 선택행이 있어야 동작. roleId 는 disable (PK 변경 금지). */
  const openEdit = () => {
    const idx = ds.selectedIndex
    if (idx < 0) return
    setEditingIdx(idx)
    form.setFieldsValue(ds.rows[idx].data)
    setModalOpen(true)
  }

  /** 선택 역할 삭제 — DataSet 변경 추적 (C 행은 즉시 제거, U 행은 _state='D'). */
  const del = () => {
    if (ds.selectedIndex >= 0) ds.deleteRow(ds.selectedIndex)
  }

  /** Modal 제출 — 신규 모드면 addRow, 수정 모드면 updateRow. */
  const onSubmit = (values: RoleRow) => {
    if (editingIdx < 0) ds.addRow(values)
    else ds.updateRow(editingIdx, values)
    setModalOpen(false)
  }

  /** 벌크 저장 — adminRole/saveRole (C/U/D 변경분만 전송). 성공 시 목록 재조회. */
  const save = async () => {
    const res = await transaction({
      id: 'save',
      service: 'adminRole/saveRole',
      action: 'save',
      inds: { ds_roleList: ds },
      outds: { ds_roleList: ds }
    })
    if (res.success) search()
  }

  const { t } = useLabel()
  const columns = useMemo(() => {
    const specs: ColumnSpec<TrackedRow<RoleRow>>[] = [
      { key: 'COL_ROLE_ID', dataIndex: ['data', 'roleId'], width: 140 },
      { key: 'COL_ROLE_NAME', dataIndex: ['data', 'roleName'], width: 180 },
      { key: 'COL_DESCRIPTION', dataIndex: ['data', 'description'] },
      {
        key: 'COL_USE_YN',
        dataIndex: ['data', 'useYn'],
        width: 80,
        render: (v) => (
          <Tag color={v === 'Y' ? 'green' : 'red'}>
            {v === 'Y' ? t('LBL_YES', '사용') : t('LBL_NO', '미사용')}
          </Tag>
        )
      },
      { key: 'COL_USER_COUNT', dataIndex: ['data', 'userCount'], width: 90 },
      {
        key: 'COL_STATUS',
        dataIndex: '_state',
        width: 70,
        render: (s) =>
          s === 'C' ? (
            <Tag color="green">{t('TTL_ADD', '신규')}</Tag>
          ) : s === 'U' ? (
            <Tag color="orange">{t('TTL_EDIT', '수정')}</Tag>
          ) : null
      }
    ]
    return makeColumns(specs, t)
  }, [t])

  return (
    <>
      <AppPage
        search={{
          form: searchForm,
          onSearch: () => search(),
          content: (
            <Form.Item label={t('COL_ROLE_NAME', '역할명')} name="roleName">
              <FormInput placeholder={t('COL_ROLE_NAME', '역할명')} />
            </Form.Item>
          )
        }}
        toolbar={{
          onSearch: () => search(),
          onAdd: openAdd,
          onEdit: openEdit,
          onDelete: del,
          onSave: save,
          dirty: ds.isDirty
        }}
      >
        <DataTable<TrackedRow<RoleRow>>
          rowKey="_rowId"
          dataSource={ds.getVisibleRows()}
          columns={columns}
          rowSelection={{
            type: 'radio',
            selectedRowKeys: ds.selectedRow ? [ds.selectedRow._rowId] : [],
            onChange: (keys) => {
              const idx = ds.rows.findIndex((r) => r._rowId === keys[0])
              ds.setSelectedIndex(idx)
            }
          }}
        />
      </AppPage>
      <Modal
        title={editingIdx < 0 ? t('TTL_ADD', '역할 추가') : t('TTL_EDIT', '역할 수정')}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
      >
        <Form form={form} layout="vertical" onFinish={onSubmit}>
          <Form.Item label={t('COL_ROLE_ID', '역할ID')} name="roleId" rules={[{ required: true }]}>
            <Input disabled={editingIdx >= 0} />
          </Form.Item>
          <Form.Item
            label={t('COL_ROLE_NAME', '역할명')}
            name="roleName"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item label={t('COL_DESCRIPTION', '설명')} name="description">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item
            label={t('LBL_USE_YN', '사용여부')}
            name="useYn"
            getValueFromEvent={(v) => (v ? 'Y' : 'N')}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}
